import { useMemo, useState } from 'react';
import { IconAlertTriangle, IconBell, IconCheck, IconChecklist, IconDots, IconPencil, IconPlus, IconSearch, IconTruckDelivery, IconUserPlus } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useNavigate } from '@tanstack/react-router';
import { DockPanel, HeaderTool, Page, PageHeader, useDock, useShell } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { UNASSIGNED, isOutside, isRisky, kpis, matchTrip, onTime, progress, stepTones, volume, type StepTone } from './overview';
import { filterTrips, stepLabel, tripCounts, type TripFilter } from './trip';
import { PhoneCreate, PhoneToday } from './PhoneToday';
import type { Trip } from './types';
import { useT } from '@hana/i18n';
import './i18n';
import s from './tms.module.css';

// Mint-TMS (desktop 1536) · tab "Điều độ hôm nay". Chỉ đọc; nút ghi báo toast.
// Chưa làm: tab Kế hoạch / Chuyến / Phương tiện / Tuyến / Ca trực (F-03b → F-05), lọc theo chủ xe / chế độ, tạo chuyến, sửa nhanh,
// dock chi tiết chuyến. Bản đồ là khung giữ chỗ (chưa nối VietMap). P0 + ghi chú sự cố lấy mẫu theo board.

type Tab = 'today' | 'plan' | 'trips' | 'fleet' | 'routes' | 'duty';
const TABS: ReadonlyArray<{ key: Tab; label: string; n?: number }> = [
  { key: 'today', label: 'Điều độ hôm nay', n: 11 },
  { key: 'plan', label: 'Kế hoạch ngày mai', n: 9 },
  { key: 'trips', label: 'Chuyến' },
  { key: 'fleet', label: 'Phương tiện & bảo trì', n: 3 },
  { key: 'routes', label: 'Tuyến, điểm & depot' },
  { key: 'duty', label: 'Ca trực' },
];

const P0 = {
  title: 'TR-2610-0315 · cont lạnh 40RF mất điện trên đường về Cái Mép',
  note: 'Tài xế Trần Văn Hải báo 13:12 · nhiệt độ −18 → −14 °C · Thuỷ sản Minh Hải · HANA-SE-2610-0130',
  esc: 'Chưa ai nhận · 4′ · 15′ sẽ gọi PHONG',
};

type DockKey = 'inc' | 'task' | 'noti';
const DOCK_TABS: ReadonlyArray<{ key: DockKey; label: string; n: number }> = [
  { key: 'inc', label: 'Sự cố', n: 2 },
  { key: 'task', label: 'Việc', n: 4 },
  { key: 'noti', label: 'Thông báo', n: 3 },
];
// [tiêu đề, mô tả, màu chấm, đậm, nút] — dữ liệu mẫu của board Mint-TMS (bảng phải ≥ 1800)
const DOCK_ITEMS: Record<DockKey, ReadonlyArray<[string, string, string, boolean, string]>> = {
  inc: [
    ['TR-2610-0315 · cont lạnh mất điện', 'P0 · 13:12 · chưa nhận', 'var(--err)', true, 'Tôi xử lý'],
    ['TR-2610-0316 · chậm giao kho 25′', 'Kẹt cổng KCN · tài xế báo 13:40', 'var(--d-amber)', true, 'Báo CS'],
    ['TR-2610-0301 · dead run (20/Oct)', 'Do khách · phí 1,20 tr chờ khách xác nhận', 'var(--lb)', false, ''],
  ],
  task: [
    ['TVA · gán xe 4 chuyến ngày mai', 'hạn 16:00 hôm nay', 'var(--d-amber)', true, 'Mở'],
    ['TFN · finish TR-2610-0309', 'ngày đóng + 1 ngày làm việc · quá hạn 2h', 'var(--err)', true, 'Mở'],
    ['Đăng kiểm 51D-140.77', 'hết hạn 29/Oct/2026', 'var(--d-amber)', false, ''],
    ['Phù hiệu 51D-221.09', 'hết hạn 25/Oct/2026', 'var(--err)', false, ''],
  ],
  noti: [
    ['CS MAI: ETD HANA-SE-2610-0142 dời +1 ngày', 'Chuyến TR-2610-0311 giữ nguyên giờ hạ CY', 'var(--pri)', true, ''],
    ['Tài xế Công gửi 3 ảnh biên nhận', 'TR-2610-0311 · dầu, cầu đường', 'var(--lb)', false, ''],
    ['Nhà xe Quỳnh Vy xác nhận 2 xe ngày mai', '23/Oct 13:00', 'var(--lb)', false, ''],
  ],
};

const PINS: ReadonlyArray<[string, string, string, 'blue' | 'err' | 'amber' | 'green']> = [
  ['51C-628.14', '42%', '30%', 'blue'],
  ['51D-140.77', '24%', '58%', 'blue'],
  ['51C-903.55', '78%', '74%', 'err'],
  ['51D-221.09', '60%', '42%', 'amber'],
  ['51C-377.40', '18%', '22%', 'blue'],
  ['51D-512.83', '88%', '18%', 'green'],
  ['51C-771.20', '34%', '78%', 'blue'],
  ['51C-118.62', '70%', '20%', 'blue'],
];
const PIN_VAR = { blue: 'var(--d-blue)', err: 'var(--err)', amber: 'var(--d-amber)', green: 'var(--d-green)' } as const;
const SLICE_VAR = { idle: 'color-mix(in srgb, var(--d-blue) 55%, var(--card))', run: 'var(--d-blue)', risk: 'var(--err)', done: 'var(--d-green)' } as const;
const STEP_CLS: Record<StepTone, string> = { done: s.stDone!, bad: s.stBad!, ok: s.stOk!, late: s.stLate!, run: s.stRun!, todo: '' };

const FILTERS: ReadonlyArray<{ key: TripFilter; label: string }> = [
  { key: 'all', label: 'Tất cả' },
  { key: 'run', label: 'Đang chạy' },
  { key: 'risk', label: 'Rủi ro' },
  { key: 'done', label: 'Xong' },
];

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function statusText(t: Trip): { k: string; cls: string } {
  if (t.step >= 6) return { k: 'Xong · cập nhật 10:42', cls: '' };
  if (t.risk === 'p0') return { k: `Sự cố: cont lạnh mất điện tại ${stepLabel(t.step)}`, cls: s.stTxErr! };
  if (t.risk === 'late') return { k: `${stepLabel(t.step)} · chậm so kế hoạch`, cls: s.stTxWarn! };
  return { k: `Đang: ${stepLabel(t.step)}`, cls: '' };
}

const contLine = (t: Trip) => {
  const first = t.containerNos[0] ?? '';
  const more = t.containerNos.length > 1 ? ` +${t.containerNos.length - 1}` : '';
  return `${t.jobId} · ${t.cont}${first ? ` · ${first}${more}` : ''}${t.mode !== 'Nguyên chuyến' ? ` · ${t.mode}` : ''}${t.surcharge ? ` · PT ${t.surcharge}` : ''}`;
};

const flagCls = (t: Trip) => (t.risk === 'p0' ? s.flagErr : t.flag === 'ghép chuyến' ? s.flagInfo : '');

function TodayTab({ trips, p0Done, setP0Done, inDock }: { trips: readonly Trip[]; p0Done: boolean; setP0Done: (v: boolean) => void; inDock: boolean }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<TripFilter>('all');
  const [q, setQ] = useState('');
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  const found = useMemo(() => trips.filter((t) => matchTrip(t, q)), [trips, q]);
  const counts = tripCounts(found);
  const rows = filterTrips(found, filter);
  const ks = kpis(trips, UNASSIGNED);
  const prog = progress(trips);
  const ot = onTime(trips);
  const vol = volume(trips);
  const avg = vol.avg.toFixed(1).replace('.', ',');
  const openTrip = (t: Trip) => void navigate({ to: '/ezy/tms/trips/$tripId', params: { tripId: t.id } });

  return (
    <>
      {!p0Done && !inDock && (
        <div role="alert" className={s.p0}>
          <span className={s.p0Badge}>P0</span>
          <div className={s.p0Tx}>
            <b>{P0.title}</b>
            <div className={s.sub}>{P0.note}</div>
          </div>
          <span className={s.p0Esc}>{P0.esc}</span>
          <Button variant="primary" icon={<IconCheck size={16} />} onClick={() => setP0Done(true)}>
            {t('tms.handle')}
          </Button>
        </div>
      )}
      <div className={s.kpis}>
        {ks.map((k) => (
          <button key={k.key} type="button" className={`${s.kpi} ${k.filter && k.filter === filter ? s.kpiOn : ''}`} onClick={() => (k.filter ? setFilter(k.filter) : noWrite())}>
            <div className={s.kpiL}>{k.label}</div>
            <div className={s.kpiV}>
              <b className={k.tone === 'err' ? s.toneErr : k.tone === 'warn' ? s.toneWarn : ''}>{k.value}</b>
              <span>{k.unit}</span>
            </div>
          </button>
        ))}
      </div>
      <div className={s.charts}>
        <section aria-label="Sản lượng chuyến" className={s.card}>
          <div className={s.cardHd}>
            <h2 className={s.h2}>{t('tms.ov.volume')}</h2>
            <span className={s.legend}>
              <span><i className={s.lgLine} />{t('tms.ov.perDay')}</span>
              <span><i className={s.lgDash} />{t('tms.ov.outside')}</span>
            </span>
          </div>
          <svg role="img" aria-label={`Số chuyến 14 ngày từ 09/Oct tới 22/Oct, trung bình ${avg} chuyến/ngày, hôm nay ${trips.length}`} width="100%" height="118" viewBox="0 0 460 118" className={s.chart}>
            <line x1="12" y1="94" x2="448" y2="94" className={s.axis} />
            <line x1="12" y1="54" x2="448" y2="54" className={s.grid} />
            <path d={vol.all.area} fill="var(--d-blue)" opacity="0.08" />
            <path d={vol.all.line} fill="none" stroke="var(--d-blue)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d={vol.outside} fill="none" stroke="var(--d-amber)" strokeWidth="2" strokeDasharray="5 5" />
            <circle cx={vol.all.last.x} cy={vol.all.last.y} r="4.5" fill="var(--card)" stroke="var(--d-blue)" strokeWidth="2.5" />
            <text x={vol.all.last.x} y={(vol.all.last.y - 10).toFixed(1)} textAnchor="end" className={s.val}>{trips.length}</text>
            {vol.axis.map((x) => (
              <text key={x.k} x={x.x} y="112" textAnchor="middle" className={s.tick}>{x.k}</text>
            ))}
          </svg>
          <div className={s.note}>{`TB ${avg} chuyến/ngày · 214 chuyến tháng 10 · CN và T7 chiều ít chuyến`}</div>
        </section>
        <section aria-label="Xe hoạt động" className={s.card}>
          <h2 className={s.h2}>{t('tms.ov.byProgress')}</h2>
          <div className={s.donut}>
            <svg role="img" aria-label={`${trips.length} chuyến hôm nay: ${prog.map((p) => `${p.label} ${p.n}`).join(', ')}`} width="104" height="104" viewBox="0 0 132 132">
              <circle cx="66" cy="66" r="52" fill="none" stroke="var(--line)" strokeWidth="16" />
              {prog.map((p) => (
                <circle key={p.key} cx="66" cy="66" r="52" fill="none" stroke={SLICE_VAR[p.key]} strokeWidth="16" strokeDasharray={p.dash} strokeDashoffset={p.off} transform="rotate(-90 66 66)" />
              ))}
              <text x="66" y="68" textAnchor="middle" fontSize="22" fontWeight="550" fill="var(--tx)">{trips.length}</text>
              <text x="66" y="85" textAnchor="middle" className={s.tick}>{t('tms.ov.trips')}</text>
            </svg>
            <div className={s.legs}>
              {prog.map((p) => (
                <button key={p.key} type="button" className={s.leg} onClick={() => setFilter(p.filter)}>
                  <span className={s.legDot} style={{ background: SLICE_VAR[p.key] }} />
                  <span className={s.legK}>{p.label}</span>
                  <b className={s.legN}>{p.n}</b>
                </button>
              ))}
              <div className={s.note}>{`${trips.filter((x) => !isOutside(x)).length} xe nhà · ${trips.filter(isOutside).length} nhà xe`}</div>
            </div>
          </div>
        </section>
        <section aria-label="Đúng giờ" className={s.card}>
          <h2 className={s.h2}>{t('tms.ov.onTime')}</h2>
          <svg role="img" aria-label={`Đúng giờ hôm nay ${ot.pct}%, ${ot.onTime}/${trips.length} chuyến`} width="170" height="93" viewBox="0 0 190 104" className={s.semi}>
            <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--line)" strokeWidth="14" strokeLinecap="round" />
            <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" stroke="var(--d-blue)" strokeWidth="14" strokeLinecap="round" strokeDasharray="251.3 251.3" strokeDashoffset={ot.offset} />
            <text x="95" y="82" textAnchor="middle" fontSize="28" fontWeight="550" fill="var(--tx)">{ot.pct}%</text>
          </svg>
          <div className={`${s.note} ${s.center}`}>{`${ot.onTime}/${trips.length} chuyến đúng hẹn lấy / hạ · ${ot.late} trễ hoặc sự cố`}</div>
        </section>
      </div>
      <div className={s.main}>
        <section aria-label="Chuyến hôm nay theo xe" className={s.table}>
          <div className={s.tHd}>
            <h2 className={s.h2}>{t('tms.ov.todayTrips')}</h2>
            <div className={s.tFilter} role="group" aria-label="Lọc chuyến">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" aria-pressed={filter === f.key} className={s.tf} onClick={() => setFilter(f.key)}>
                  {f.label} {counts[f.key]}
                </button>
              ))}
            </div>
          </div>
          <div className={s.tBar}>
            <label className={s.search}>
              <IconSearch size={15} stroke={2.2} aria-hidden="true" />
              <input aria-label="Tìm lệnh" placeholder="Số lệnh / job / số cont / biển số" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Button variant="soft" className={s.fbtn} icon={<IconPlus size={16} />} onClick={noWrite}>
              {t('tms.filter')}
            </Button>
          </div>
          {rows.length === 0 && <div className={s.none}>{`Không có lệnh khớp "${q}" và bộ lọc. Tìm theo số lệnh TR-…, JobID, số cont hoặc biển số.`}</div>}
          <table className={s.tbl}>
            <thead>
              <tr>
                <th style={{ width: 150 }}>{t('tms.ov.thVehicle')}</th>
                <th style={{ width: 168 }}>{t('tms.ov.thTrip')}</th>
                <th>{t('tms.ov.thGps')}</th>
                <th style={{ width: 96 }}>{t('tms.ov.thDue')}</th>
                <th style={{ width: 86 }}>Dispatcher</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const st = statusText(t);
                const hot = isRisky(t);
                return (
                  <tr key={t.id} data-row onClick={() => openTrip(t)}>
                    <td>
                      <div className={s.truck}>{t.vehicle}</div>
                      <div className={s.s2} title={t.crew}>{t.crew}</div>
                    </td>
                    <td>
                      <span className={s.id}>{t.id}</span>
                      {t.flag && <span className={`${s.flag} ${flagCls(t)}`} title={t.flag}>{t.flag}</span>}
                      <div className={s.s2} title={contLine(t)}>{contLine(t)}</div>
                    </td>
                    <td>
                      <div className={s.steps}>
                        {stepTones(t).map((tone, j) => (
                          <i key={j} className={STEP_CLS[tone]} title={stepLabel(j)} />
                        ))}
                      </div>
                      <div className={`${s.stTx} ${st.cls}`} title={st.k}>{st.k}</div>
                    </td>
                    <td className={hot ? s.dueHot : ''}>{t.due}</td>
                    <td className={s.disp}>{t.dispatcher}</td>
                    <td>
                      <button type="button" className={s.more} aria-label={`Sửa nhanh ${t.id}`} title="Sửa nhanh giờ · xe · điểm hạ" onClick={(e) => { e.stopPropagation(); noWrite(); }}>
                        <IconPencil size={15} stroke={2} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className={s.foot}>{`${rows.length} chuyến${q ? ' khớp tìm / lọc' : ''} · xe nhà và nhà xe ngoài`}</div>
        </section>
        <div className={s.side}>
          <section aria-label="Bản đồ xe" className={`${s.card} ${s.mapCard}`}>
            <div className={s.cardHd} style={{ marginBottom: 10 }}>
              <h2 className={s.h2}>{t('tms.ov.map')}</h2>
              <span className={s.note}>{t('tms.ov.mapNote')}</span>
            </div>
            <div role="img" aria-label={`Khung bản đồ giữ chỗ, ${PINS.length} xe`} className={s.map}>
              <svg aria-hidden="true" width="100%" height="250" viewBox="0 0 332 250" preserveAspectRatio="none">
                <path d="M0 70 C 80 60, 140 110, 332 90" className={s.road} strokeWidth="7" />
                <path d="M60 250 C 90 170, 170 140, 240 0" className={s.road} strokeWidth="5" />
                <path d="M0 190 C 120 170, 220 200, 332 160" className={s.road} strokeWidth="4" />
              </svg>
              {PINS.map(([k, x, y, c]) => (
                <span key={k} className={s.pin} title={k} style={{ left: x, top: y, background: PIN_VAR[c] }} />
              ))}
              <span className={s.mapLb}>{t('tms.mapLabel')}</span>
            </div>
          </section>
          <section aria-label="Chưa gán xe" className={`${s.card} ${s.un}`}>
            <div className={s.cardHd}>
              <h2 className={s.h2}>{t('tms.ov.unassigned')}</h2>
              <span className={s.unN}>{UNASSIGNED.length} chuyến</span>
            </div>
            {UNASSIGNED.map((u) => (
              <div key={u.id} className={s.unRow}>
                <div className={s.unTx}>
                  <b>{u.id}</b>
                  {u.urgent && <span className={s.urg}>{t('tms.ov.urgent')}</span>}
                  <div className={s.s2} title={u.desc}>{u.desc}</div>
                </div>
                <Button variant="soft" icon={<IconUserPlus size={16} />} onClick={noWrite}>
                  {t('tms.assign')}
                </Button>
              </div>
            ))}
          </section>
        </div>
      </div>
    </>
  );
}

export function TmsPage() {
  const t = useT();
  const toast = useToast();
  const { data, error, isLoading, isError, refetch } = useList('trip', { limit: 500 });
  const forced = readState();
  const [tab, setTab] = useState<Tab>('today');
  const [p0Done, setP0Done] = useState(false);
  const dock = useDock<DockKey>('inc');
  const trips = data?.items ?? [];

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (trips.length === 0) state = 'empty';

  if (useShell().layout === 'phone') {
    return (
      <Page>
        <PageHeader title="Vận tải" actions={<PhoneCreate />} />
        <PhoneToday state={state} onRetry={() => void refetch()} />
      </Page>
    );
  }

  return (
    <Page
      kind="table"
      dock={
        dock.wide && dock.open && state !== 'loading' && state !== 'error'
          ? (
            <DockPanel
              tabs={DOCK_TABS}
              tab={dock.tab}
              onTab={dock.show}
              onClose={dock.close}
              top={
                !p0Done ? (
                  <div role="alert" className={s.p0Dock}>
                    <div className={s.p0DockHd}>
                      <span className={s.p0DockBadge}>P0</span>
                      <span className={s.p0Esc}>{P0.esc}</span>
                    </div>
                    <b>{P0.title}</b>
                    <div className={s.sub}>{P0.note}</div>
                    <Button variant="primary" className={s.p0DockBtn} icon={<IconCheck size={16} />} onClick={() => setP0Done(true)}>
                      {t('tms.handle')}
                    </Button>
                  </div>
                ) : undefined
              }
            >
              {DOCK_ITEMS[dock.tab].map(([k, v, c, bold, ak]) => (
                <div key={k} className={s.dkRow}>
                  <span className={s.dkDot} style={{ background: c }} />
                  <div className={s.dkTx}>
                    <div style={{ fontWeight: bold ? 600 : 450 }}>{k}</div>
                    <div className={s.sub}>{v}</div>
                  </div>
                  {ak && !(p0Done && ak === 'Tôi xử lý') && (
                    <button type="button" className={`${s.dkAct} ${ak === 'Tôi xử lý' ? s.dkActErr : ''}`} onClick={() => (ak === 'Tôi xử lý' ? setP0Done(true) : toast({ msg: 'Chưa mở ghi ở bản này' }))}>
                      {ak}
                    </button>
                  )}
                </div>
              ))}
            </DockPanel>
          )
          : undefined
      }
    >
      <PageHeader findPad
        title="Vận tải"
        tabs={
          <span className={s.duty}>
            <span className={s.dot} />
            {t('tms.dutyThanh')}
          </span>
        }
        actions={
          <>
            <HeaderTool label="Sự cố" icon={IconAlertTriangle} n={2} onClick={() => dock.toggle('inc')} />
            <HeaderTool label="Việc" icon={IconChecklist} n={4} onClick={() => dock.toggle('task')} />
            <HeaderTool label="Thông báo" icon={IconBell} n={3} onClick={() => dock.toggle('noti')} />
            <HeaderTool label="Thao tác khác" icon={IconDots} />
            <Button variant="primary" icon={<IconPlus size={16} />} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>
              {t('tms.createTrip')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        <div className={s.tabs}>
          <Segmented label="Mục vận tải" value={tab} onChange={setTab} items={TABS.map((t) => ({ key: t.key, label: t.label, n: t.n }))} />
        </div>
        <StateView
          state={tab === 'today' ? state : 'empty'}
          icon={<IconTruckDelivery size={26} stroke={1.8} />}
          title={tab !== 'today' ? t('tms.ov.notOpen') : state === 'empty' ? t('tms.ov.noTripsToday') : undefined}
          action={state === 'error' ? <Button variant="secondary" onClick={() => void refetch()}>{t('tms.ov.retry')}</Button> : undefined}
          skeleton={
            <>
              <div className={s.skelCards}>
                {[1, 2, 3, 4].map((k) => <div key={k} className={s.skel} style={{ height: 92 }} />)}
              </div>
              <div className={s.skel} style={{ marginTop: 14, padding: 18 }}><Skeleton /></div>
            </>
          }
        >
          <TodayTab trips={trips} p0Done={p0Done} setP0Done={setP0Done} inDock={dock.wide && dock.open} />
        </StateView>
      </div>
    </Page>
  );
}
